import { AddressModel, DivisionListModel, ICDCorporationListRM, IcdCorporationManager, IcdOfficeRoleModel, IcdOfficeUpdateDTO, PhoneModel, RoleListModel, StateProvinceModel, TeamListResponse, CKERetailToggleModel } from '@herbjoy/apex-api-models';
import { IFormController, ILogService, IPromise, IQService } from 'angular';
import { StateService } from 'angular-ui-router';
import { DivisionService, IcdCorporationService, IcdOfficeResponse, IcdOfficeService, NationalConsultantService, PromotingOwnerService, RegionalConsultantService, RoleService, StateProvinceService, TeamService } from '../../../core/services';
import { UtilityService } from '../../../core/services/utility.service';
import templateUrl from './edit-icd-office.component.html';

interface Names {
    FirstName?: string;
    LastName?: string;
    FullName?: string;
}

interface IcdOfficeRoleVM extends IcdOfficeRoleModel {
    Role?: RoleVM;
}

type RoleVM = RoleListModel & {
    TeamsInRole?: TeamListResponse[];
}

interface IcdOfficeVM extends IcdOfficeResponse {
    Divisions?: DivisionAndProfile[];
    IcdCorporation?: ICDCorporationListRM;
    OfficeRoles?: IcdOfficeRoleVM[];
    OfficeAddress?: AddressModel & {
        StateProvince?: StateProvinceModel;
    };
}

interface DivisionAndProfile {
    DivisionID: number;
    DivisionProfileID: number;
    DivisionName: string;
    ProfileName: string;
    Value: string;
    Text: string;
}

export const EditIcdOfficeComponent = {
    templateUrl,
    controllerAs: "vm",
    bindings: {
        icdOfficeID: "<icdOfficeId",
        icdCorporationID: "<?icdCorporationId"
    },
    controller: class EditIcdOfficeController {
        //Input
        icdOfficeID: number;
        //Input
        icdCorporationID: number;

        availableDivisionsAndProfiles: DivisionAndProfile[];
        divisions: DivisionListModel[];
        divisionsAndProfiles: DivisionAndProfile[];
        editForm: IFormController;
        icdCorporations: ICDCorporationListRM[];
        ckeInfo: CKERetailToggleModel[];
        activeckeInfo: CKERetailToggleModel;
        icdOffice: IcdOfficeVM;
        isRetail: boolean;
        isSyncingRoster: boolean;
        promotingOwners: IcdCorporationManager[];
        nationalConsultants: IcdCorporationManager[];
        regionalConsultants: IcdCorporationManager[];
        stateProvinces: StateProvinceModel[];
        constructor(private $q: IQService, private $state: StateService, private $log: ILogService,
            private divisionService: DivisionService,
            private icdOfficeService: IcdOfficeService, private icdCorporationService: IcdCorporationService,
            private nationalConsultantService: NationalConsultantService,
            private regionalConsultantService: RegionalConsultantService,
            private roleService: RoleService, private stateProvinceService: StateProvinceService,
            private teamService: TeamService, private utilityService: UtilityService,
            private promotingOwnerService: PromotingOwnerService) {
            'ngInject'

        }

        $onInit() {
            const loaded = this.refreshScreen();
            this.utilityService.showLoadingUntilResolved(
                this.utilityService.alertOnError(loaded, "Initial load failed"));
        }

        onMoveMeridianID() {
            this.$state.go(".moveMeridianID");
        }

        onTelemapperSync() {
            this.icdOfficeService.triggerTelemapperSync(this.icdOffice.MeridianOfficeID)
        }

        onCKESync() {
            this.icdOfficeService.triggerCKESync(this.icdOffice.MeridianOfficeID)
        }

        private refreshScreen() {

            const getIcdOffice: IPromise<IcdOfficeVM> = this.icdOfficeID
                ? this.icdOfficeService.retrieveIcdOffice(this.icdOfficeID)
                : this.$q.resolve(this.createNewIcdOffice()) as IPromise<IcdOfficeVM>;
            const getDivisions = this.divisionService.retrieveDivisions({})
                .then(divisions => this.sortDivisions(divisions));

            //const getCKERetailToggles = this.icdOfficeService.retrieveCKERetailInfo()
            //    .then(cke => console.log(cke));

            const getDivisionProfiles = this.divisionService.retrieveDivisionProfiles({})
            const getIcdCorporations = this.icdCorporationService.retrieveIcdCorporations({})
                .then(corporations => this.sortIcdCorporations(corporations));
            const getNationalConsultants = this.nationalConsultantService.retrieveNationalConsultants()
                .then((names: IcdCorporationManager[]) => this.assignFullNames(names));
            const getRegionalConsultants = this.regionalConsultantService.retrieveRegionalConsultants()
                .then((names: IcdCorporationManager[]) => this.assignFullNames(names));
            const getPromotingOwners = this.promotingOwnerService.retrievePromotingOwners()
                .then((names: IcdCorporationManager[]) => this.assignFullNames(names));

            const loadStateProvinces = this.stateProvinceService.retrieveStateProvinces()
                .then((stateProvinces: StateProvinceModel[]) => this.stateProvinces = stateProvinces);
            const getGlobalOfficeRoles = this.roleService.retrieveRoles()
                .then((roles: RoleListModel[]) => {
                    return roles.filter(role => {
                        return role.GlobalRole && role.IsOfficeRole;
                    });
                });
            const loadOfficeRoles = this.$q.all([getIcdOffice, getGlobalOfficeRoles])
                .then(([icdOffice, roles]) => this.getOfficeRoleData(icdOffice, roles));
            const getDivisionsAndProfiles = this.$q.all([getDivisions, getDivisionProfiles])
                .then(([divisions, profiles]) => {
                    // Combine divisions with and without profiles into a single list.
                    const res = divisions.map(function (division) {
                        return {
                            DivisionID: division.Id,
                            DivisionProfileID: null,
                            DivisionName: division.Name,
                            ProfileName: null,
                            Value: kendo.format("{0}", division.Id),
                            Text: kendo.format("{0} (no profile)", division.Name)
                        }
                    });
                    return res.concat(profiles.map(function (profile) {
                        return {
                            DivisionID: profile.DivisionID,
                            DivisionProfileID: profile.DivisionProfileID,
                            DivisionName: profile.DivisionName,
                            ProfileName: profile.Name,
                            Value: kendo.format("{0}_{1}", profile.DivisionID, profile.DivisionProfileID),
                            Text: kendo.format("{0} ({1} profile)", profile.DivisionName, profile.Name)
                        };
                    }));
                })
                .then<DivisionAndProfile[]>(divisionsAndProfiles => {
                    return divisionsAndProfiles.sort(function (a, b) {
                        if (a.DivisionName < b.DivisionName) {
                            return -1;
                        } else if (a.DivisionName > b.DivisionName) {
                            return 1;
                        }
                        if (a.ProfileName < b.ProfileName) {
                            return -1;
                        } else if (a.ProfileName > b.ProfileName) {
                            return -1;
                        }
                        return a.DivisionProfileID - b.DivisionProfileID;
                    })
                })
            var loadCKE = this.icdOfficeService.retrieveCKERetailInfo();

            const loadIcdOfficeData = this.$q.all([getIcdOffice, getIcdCorporations, getDivisions,
                getNationalConsultants, getRegionalConsultants, getDivisionsAndProfiles, getPromotingOwners, loadCKE])
                .then(([icdOffice, icdCorporations, divisions, ncs, rcs, divisionProfiles, pos, cke]) => {
                    // Load all of the resolved data into the controller.
                    this.ckeInfo = cke;
                    return this.loadResolvedIcdOfficeData(icdOffice, icdCorporations, divisions, ncs, rcs,
                        divisionProfiles, pos, cke);
                });

            return this.$q.all([loadIcdOfficeData, loadStateProvinces, loadOfficeRoles]);
        }

        private createNewIcdOffice(): Partial<IcdOfficeVM> {
            return {
                IcdCorporationID: this.icdCorporationID,
                OfficeRoles: [],
                OfficeDivisions: { Divisions: {} },
                Divisions: [],
                PromotingOwners: [],
                OfficeAddress: {} as AddressModel,
                OfficePhone: {} as PhoneModel,
                OfficeFax: {} as PhoneModel
            };
        }

        onSubmit() {
            const office = this.icdOffice;
            const request = this.buildRequest(office);
            let save: IPromise<{ Id: number }>;
            if (office.Id) {
                save = this.icdOfficeService.updateIcdOffice(office.Id, request)
                    .then(() => ({ Id: office.Id }));
            } else {
                save = this.icdOfficeService.createIcdOffice(request);
            }
            const refreshed = save.then(response => {
                if (this.activeckeInfo != null && this.activeckeInfo.ICDOfficeID == null) {
                    this.activeckeInfo.ICDOfficeID = response.Id;
                }
                return this.icdOfficeService.updateCKERetailInfo(this.activeckeInfo).then(update => {
                    if (update) {
                        return response; // Pass original response forward
                    } else {
                        //this.utilityService.alertOnError(
                        //    this.refreshScreen(),
                        //    "Save was successful, but updating toggle failed. Please try again"
                        //);
                        return Promise.reject('Update failed'); // stop further .then() flow
                    }
                });
            }).then(response => {

                if (!office.Id && response.Id) {
                    this.$state.go("employee.new", { icdOfficeID: response.Id })
                        .then(() => { });
                } else {
                    this.editForm.$setPristine();
                    return this.utilityService.alertOnError(this.refreshScreen(),
                        "Save was successful, but reloading data failed. Please refresh manually.");
                }
            });

            save = this.utilityService.alertOnError(save, "Save failed");
            this.utilityService.showLoadingUntilResolved(this.$q.all([
                save, refreshed]));
        }

        private loadResolvedIcdOfficeData(icdOffice: IcdOfficeVM, icdCorporations: ICDCorporationListRM[],
            divisions: DivisionListModel[], nationalConsultants: IcdCorporationManager[],
            regionalConsultants: IcdCorporationManager[], divisionsAndProfiles: DivisionAndProfile[],
            promotingOwners: IcdCorporationManager[], cke: CKERetailToggleModel[]) {
            icdOffice.OfficeEmail = (icdOffice.OfficeEmail || "").trim(); // Untrimmed whitespace trips up the email validation.
            this.icdCorporationID = icdOffice.IcdCorporationID;
            this.divisions = divisions;
            this.divisionsAndProfiles = divisionsAndProfiles;
            this.icdCorporations = icdCorporations.filter(corporation => {
                // Only allow the single corporation to be selected if the corporation ID was passed in.
                return !this.icdCorporationID || corporation.Id === this.icdCorporationID
                    // Also show the office's current corporation, if any.
                    || corporation.Id === icdOffice.IcdCorporationID;
            });
            this.nationalConsultants = nationalConsultants;
            this.regionalConsultants = regionalConsultants;
            this.promotingOwners = promotingOwners;

            // Set original corporation.
            icdOffice.IcdCorporation = icdCorporations.filter(function (corporation) {
                return corporation.Id === icdOffice.IcdCorporationID;
            })[0] || (this.icdCorporationID ? icdCorporations[0] : null);

            // Set original divisions.
            icdOffice.Divisions = Object.values(icdOffice.OfficeDivisions.Divisions).map(d => {
                return divisionsAndProfiles.find(dap => {
                    return dap.DivisionProfileID === d.DivisionProfileID && dap.DivisionID === d.DivisionID;
                });
            }).filter(function (division) {
                // Only include non-null matches.
                return division;
            });

            // Set original NC.
            icdOffice.NationalConsultant = nationalConsultants.filter(function (nc) {
                return nc.Id === icdOffice.NationalConsultantID;
            })[0];

            icdOffice.RegionalConsultant = regionalConsultants.filter(function (rc) {
                return rc.Id === icdOffice.RegionalConsultantID;
            })[0];
            icdOffice.PromotingOwners = promotingOwners.filter(function (po) {
                return icdOffice.PromotingOwners.filter(function (owner) {
                    return po.Id === owner.Id;
                })[0] != null;
            });

            this.activeckeInfo = cke.find(x => x.ICDOfficeID === icdOffice.Id && x.IsActive);
            if (this.activeckeInfo == null) {
                this.activeckeInfo = {
                    ICDOfficeID: icdOffice.Id,
                    IsRetail: false,
                    CreatedDate: new Date(),
                    IsActive: true
                };
            }
            this.icdOffice = icdOffice;
            this.updateAvailableDivisionProfiles();
        }

        //onToggleRetail() {
        //    this.activeckeInfo.IsRetail = !this.activeckeInfo.IsRetail;
        //}

        onChangeDivisions() {
            if (!this.icdOffice.Divisions) {
                this.$log.warn("A division change was triggered before the office divisions were instantiated.");
                this.icdOffice.Divisions = [];
            }
            this.updateAvailableDivisionProfiles();
        }

        onSyncRoster() {
            this.isSyncingRoster = true;
            this.icdOfficeService.createIcdOfficeRosterSyncJob(this.icdOffice.Id, null)
                .then(response => {
                    alert("The roster for this office will now sync in the background")
                })
                .catch(ex => {
                    console.error(ex)
                    alert("Unable to schedule roster import: " + this.utilityService.getExceptionMessage(ex))
                })
        }

        private updateAvailableDivisionProfiles() {
            // Add/remove divisions from list based on what's valid.
            this.availableDivisionsAndProfiles = this.divisionsAndProfiles.filter(divisionProfile => {
                // Only show the division if it's either currently selected verbatim or the division isn't selected at all.
                return this.icdOffice.Divisions.filter(officeDivision => {
                    return officeDivision.DivisionID === divisionProfile.DivisionID
                        && officeDivision.DivisionProfileID !== divisionProfile.DivisionProfileID;
                }).length === 0;
            });
        }

        private sortIcdCorporations(corporations: ICDCorporationListRM[]): ICDCorporationListRM[] {
            return corporations.sort(function (a, b) {
                return a.ClientName < b.ClientName ? -1 : 1;
            });
        }

        private sortDivisions(divisions: DivisionListModel[]): DivisionListModel[] {
            return divisions.sort(function (a, b) {
                // Sort by name.
                return a.Name < b.Name ? -1 : 1;
            });
        }

        private assignFullNames(names: Names[]) {
            names.forEach(function (name) {
                name.FullName = ((name.FirstName || "").trim() + " " + (name.LastName || "").trim()).trim();
            });
            return names;
        }

        private getOfficeRoleData(icdOffice: IcdOfficeVM, globalOfficeRoles: RoleListModel[]) {
            globalOfficeRoles.forEach(role => {
                // Auto-add the role to the office if the role has not already been added.
                this.addMissingOfficeRole(icdOffice, role);
            });

            icdOffice.OfficeRoles.forEach(function (officeRole) {
                // Load the full name and full role within the office role for easy access within the template.
                const role = globalOfficeRoles.filter(function (role) {
                    return role.Id === officeRole.RoleID;
                })[0];
                officeRole.Role = role;
            });

            return this.$q.all(icdOffice.OfficeRoles.map(officeRole => {
                // Load all of the employees for the role, then resolve the office role.
                return this.loadRoleTeams(officeRole.Role)
                    .then(function () {
                        // Map all assigned positions to the specific employee in role.
                        officeRole.AssignedPositions = officeRole.AssignedPositions.map(position =>
                            officeRole.Role.TeamsInRole.find(team => team.TeamID === position.TeamID)
                        );
                    })
            }));
        }

        private addMissingOfficeRole(icdOffice: IcdOfficeVM, role: RoleListModel) {
            let officeRole = icdOffice.OfficeRoles.filter(function (officeRole) {
                return officeRole.RoleID === role.Id;
            })[0];
            if (!officeRole) {
                officeRole = {};
                icdOffice.OfficeRoles.push(officeRole);
                officeRole.AssignedPositions = [];
            }
            officeRole.RoleID = role.Id;
            officeRole.Role = role;
            officeRole.Name = role.Name;
            officeRole.Description = role.Description;
            officeRole.Required = role.Required;
            officeRole.MinPositions = role.MinPositions;
            officeRole.MaxPositions = role.MaxPositions;
        }

        private loadRoleTeams(role: RoleVM) {
            return this.teamService.retrieveTeams({
                RoleIDs: [role.Id]
            })
                .then(function (teams) {
                    role.TeamsInRole = teams;
                    return role;
                })
        }


        private buildRequest(office: IcdOfficeVM): IcdOfficeUpdateDTO {
            const request = {
                IcdOfficeID: office.Id,
                MeridianOfficeID: office.MeridianOfficeID,
                JdeDistributionCode: office.JdeDistributionCode,
                OfficeName: office.OfficeName,
                OfficeEmail: office.OfficeEmail,
                OfficePhone: !office.OfficePhone ? null : {
                    PhoneNumber: office.OfficePhone.PhoneNumber,
                    Extension: office.OfficePhone.Extension
                },
                OfficeFax: !office.OfficeFax ? null : {
                    PhoneNumber: office.OfficeFax.PhoneNumber,
                    Extension: null
                },
                MoveInDate: office.MoveInDate,
                MoveOutDate: office.MoveOutDate,
                IcdCorporationID: !office.IcdCorporation ? null : office.IcdCorporation.Id,
                IsLegalOffice: office.IsLegalOffice,
                NationalConsultantID: !office.NationalConsultant ? null : office.NationalConsultant.Id,
                RegionalConsultantID: !office.RegionalConsultant ? null : office.RegionalConsultant.Id,
                PromotingOwnerIDs: !office.PromotingOwners ? [] : this.icdOffice.PromotingOwners.map(p => p.Id),
                OfficeDivisions: office.Divisions.map(function (division) {
                    return {
                        DivisionID: division.DivisionID,
                        DivisionProfileID: division.DivisionProfileID
                    };
                }),
                OfficeAddress: !office.OfficeAddress ? null : {
                    StreetLine1: office.OfficeAddress.StreetLine1,
                    StreetLine2: office.OfficeAddress.StreetLine2,
                    AptNum: office.OfficeAddress.AptNum,
                    City: office.OfficeAddress.City,
                    StateProvinceID: !office.OfficeAddress.StateProvince ? null
                        : office.OfficeAddress.StateProvince.StateProvinceID,
                    PostalCode: office.OfficeAddress.PostalCode,
                    CountryID: !office.OfficeAddress.StateProvince ? null : office.OfficeAddress.StateProvince.CountryID
                },
                RolePositions: office.OfficeRoles.map(function (officeRole) {
                    return {
                        RoleID: officeRole.RoleID,
                        AssignedPositions: officeRole.AssignedPositions.map(function (team) {
                            return team.TeamID;
                        })
                    };
                }),
                DefaultSgiID: office.DefaultSgiID
            }
            return request;
        }
    }
}
